// ============================================================
// ScreenZoom.tsx
// Opt-in accessibility aid: whole-content zoom + edge-pan navigation.
//
// Enabled per-user via Settings → Appearance → "Screen zoom & pan"
// (useZoomPref, localStorage-backed). When enabled, a small collapsed
// magnifier button sits bottom-right; clicking it ANIMATES/extends into
// a control pill (−  120%  +  · edge-pan toggle · ✕). The zoom applies to
// the MAIN CONTENT ONLY (this component wraps App.renderMain()); the
// sidebar, global toasts and this control are App-level siblings, so
// they are never affected.
//
// ── Crispness (why CSS `zoom`, not transform:scale) ──
// `transform: scale()` stretches an already-rasterised layer, so text
// goes soft. CSS `zoom` instead re-lays-out and RE-RASTERISES the
// content at the target size, so text stays crisp (much closer to the
// browser's own Ctrl-+ zoom). `zoom` is non-standard but supported in
// Chromium (Chrome/Edge), Safari, and Firefox ≥126 — all within the
// project's browser targets. Honest ceiling: it is crisper than
// transform:scale but still not 100% identical to native Ctrl-+ (which
// also re-evaluates media queries); raster images still bitmap-scale.
//
// Because `zoom` genuinely enlarges layout, the zoomed content overflows
// the viewport and we PAN with the viewport's own native scroll
// (scrollLeft/scrollTop) — no manual transform/bounds math, and the
// browser clamps to content edges for free.
//
// ── Stable tree (why we never early-return different structure) ──
// Toggling the enabled flag must NOT change the depth of `children` in
// the tree, or React would unmount/remount the whole main view (e.g.
// Settings, resetting its section state). So the viewport/content
// wrappers are ALWAYS rendered — inert (no zoom, native scroll) when
// disabled or at 100% — and only the control + the pan effects are gated
// on `enabled`.
//
// The zoom LEVEL is session-only (resets to 100% on mount) so a reload
// can't leave anyone stuck zoomed; only the enabled toggle persists.
// ============================================================
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useZoomPref } from '../../utils/zoomPref'

const MIN_ZOOM = 0.7
const MAX_ZOOM = 2.5
const STEP = 0.1

// Edge-pan tuning.
const DEAD_ZONE = 0.55                    // central fraction with no panning
const MARGIN = (1 - DEAD_ZONE) / 2        // pan-active band on each side (0.225)
const MAX_SPEED = 24                       // px/frame at the very edge

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))

const ScreenZoom: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabled = useZoomPref((s) => s.enabled)

  const [zoom, setZoom] = useState(1)
  const [panEnabled, setPanEnabled] = useState(true)
  const [expanded, setExpanded] = useState(false)
  // Measured, content-hugging width of the expanded pill (Issue B) — no
  // dead space. Fallback until the first layout measurement lands.
  const [expandedWidth, setExpandedWidth] = useState(248)

  const viewportRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const controlsRef = useRef<HTMLDivElement | null>(null)
  const zoomRef = useRef(1)
  const panEnabledRef = useRef(true)
  const mouseRef = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)

  useEffect(() => { zoomRef.current = zoom }, [zoom])
  useEffect(() => { panEnabledRef.current = panEnabled }, [panEnabled])

  // Apply the crisp CSS `zoom` to the content wrapper (imperative — avoids
  // React/TS unit quirks around the non-standard property). Removed at 100%
  // so the wrapper is fully inert.
  //
  // ★ ALL-DIRECTION PAN: `zoom` on a width:auto block REFLOWS the content to
  // fit the viewport width (re-wraps narrower, scales back up), so it never
  // overflows horizontally — that's why only vertical pan worked. We LOCK the
  // content to its natural unzoomed width (viewport.clientWidth) so `zoom`
  // makes it genuinely WIDER than the viewport → real horizontal overflow to
  // scroll/pan into. The locked width equals the width the content already
  // used unzoomed, so the internal responsive layout is unchanged (no reflow),
  // just uniformly scaled. clientWidth is the container's own inner width, so
  // re-reading it on each apply is stable (no scrollbar feedback loop).
  const applyZoom = useCallback((): void => {
    const c = contentRef.current
    const vp = viewportRef.current
    if (!c) return
    if (zoomRef.current === 1) {
      c.style.removeProperty('zoom')
      c.style.removeProperty('width')
      return
    }
    if (vp) c.style.width = `${vp.clientWidth}px`
    c.style.setProperty('zoom', String(zoomRef.current))
  }, [])

  // Per-axis native-scroll delta from a 0..1 pointer position: 0 in the
  // dead-zone, ramping (quadratic) toward the edges. Near the start
  // (left/top) scroll back (−); near the end (right/bottom) scroll on (+).
  const scrollDelta = (rel: number): number => {
    if (rel < MARGIN) {
      const p = (MARGIN - rel) / MARGIN
      return -(p * p) * MAX_SPEED
    }
    if (rel > 1 - MARGIN) {
      const p = (rel - (1 - MARGIN)) / MARGIN
      return p * p * MAX_SPEED
    }
    return 0
  }

  // Edge-pan loop — runs while enabled; no-ops unless zoomed, edge-pan on,
  // and the pointer is over the viewport. Drives native scroll (browser
  // clamps to content bounds automatically).
  useEffect(() => {
    if (!enabled) return
    const frame = (): void => {
      rafRef.current = requestAnimationFrame(frame)
      if (zoomRef.current <= 1 || !panEnabledRef.current) return
      const vp = viewportRef.current
      if (!vp) return
      const rect = vp.getBoundingClientRect()
      const { x, y } = mouseRef.current
      const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      if (!inside) return
      const relX = clamp((x - rect.left) / rect.width, 0, 1)
      const relY = clamp((y - rect.top) / rect.height, 0, 1)
      const dx = scrollDelta(relX)
      const dy = scrollDelta(relY)
      if (dx !== 0) vp.scrollLeft += dx
      if (dy !== 0) vp.scrollTop += dy
    }
    rafRef.current = requestAnimationFrame(frame)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [enabled])

  // Pointer tracking (one passive listener while enabled).
  useEffect(() => {
    if (!enabled) return
    const onMove = (e: MouseEvent): void => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }
    window.addEventListener('mousemove', onMove, { passive: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [enabled])

  // Apply zoom whenever the level changes; reset scroll to origin when we
  // drop back to (or below) 100% so the view isn't left off-screen.
  useEffect(() => {
    applyZoom()
    if (zoom <= 1 && viewportRef.current) viewportRef.current.scrollTo(0, 0)
    // Re-lock the natural width on resize so the zoomed content keeps
    // overflowing correctly (and pans both ways) after the window changes.
    const onResize = (): void => applyZoom()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [zoom, applyZoom])

  // Measure the expanded pill's natural content width once the control is
  // mounted (Issue B). controlsRef holds the − / % / + / pan / ✕ row; its
  // scrollWidth is its true content width even while faded out (it's still
  // laid out). Total = paddingLeft(6) + magnifier(32) + row marginLeft(2) +
  // row content + paddingRight(6).
  useLayoutEffect(() => {
    if (!enabled) return
    if (controlsRef.current) {
      setExpandedWidth(6 + 32 + 2 + controlsRef.current.scrollWidth + 6)
    }
  }, [enabled])

  const changeZoom = useCallback((next: number): void => {
    const z = clamp(Math.round(next * 100) / 100, MIN_ZOOM, MAX_ZOOM)
    zoomRef.current = z
    setZoom(z)
  }, [])

  const pct = Math.round(zoom * 100)

  const iconBtn: React.CSSProperties = {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    borderRadius: 8,
    flex: '0 0 auto',
  }

  return (
    <>
      {/* Zoom viewport — ALWAYS rendered (stable tree). Inert at 100%
          (no zoom, overflow visible so main scrolls as normal); once
          zoomed it becomes the scroll container the edge-pan drives. */}
      <div
        ref={viewportRef}
        style={{
          height: zoom > 1 ? '100%' : 'auto',
          overflow: zoom > 1 ? 'auto' : 'visible',
          position: 'relative',
        }}
      >
        <div
          ref={contentRef}
          style={{
            // Crispness hint; `zoom` itself is set imperatively (applyZoom).
            textRendering: 'optimizeLegibility',
          }}
        >
          {children}
        </div>
      </div>

      {/* Control — fixed, App-level position so it is NEVER inside the zoom
          layer. Only rendered when the setting is on. Collapsed = a 44px
          magnifier circle; expanded = a content-hugging pill the width
          transition EXTENDS out from the icon. */}
      {enabled && (
        <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 2147483000 }}>
          <div
            role="group"
            aria-label="Screen zoom controls"
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 44,
              width: expanded ? expandedWidth : 44,
              paddingLeft: 6,
              paddingRight: expanded ? 6 : 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              background: 'var(--casevo-brand)',
              color: '#fff',
              borderRadius: 9999,
              boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
              transition: 'width 280ms cubic-bezier(0.22, 1, 0.36, 1), padding 280ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <button
              type="button"
              onClick={() => { if (!expanded) setExpanded(true) }}
              aria-label={expanded ? 'Screen zoom' : 'Open screen zoom controls'}
              title="Screen zoom & pan"
              style={{ ...iconBtn, cursor: expanded ? 'default' : 'pointer' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.5" y2="16.5" />
              </svg>
            </button>

            <div
              ref={controlsRef}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginLeft: 2,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? 'auto' : 'none',
                transition: 'opacity 180ms ease',
                transitionDelay: expanded ? '110ms' : '0ms',
              }}
            >
              <button type="button" onClick={() => changeZoom(zoom - STEP)} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out" title="Zoom out" style={{ ...iconBtn, opacity: zoom <= MIN_ZOOM ? 0.4 : 1, cursor: zoom <= MIN_ZOOM ? 'default' : 'pointer' }}>−</button>
              <span style={{ minWidth: 46, textAlign: 'center', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
              <button type="button" onClick={() => changeZoom(zoom + STEP)} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in" title="Zoom in" style={{ ...iconBtn, opacity: zoom >= MAX_ZOOM ? 0.4 : 1, cursor: zoom >= MAX_ZOOM ? 'default' : 'pointer' }}>+</button>

              <span aria-hidden="true" style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.35)', margin: '0 6px', flex: '0 0 auto' }} />

              <button
                type="button"
                onClick={() => setPanEnabled((v) => !v)}
                aria-pressed={panEnabled}
                aria-label="Toggle edge-pan"
                title={panEnabled ? 'Edge-pan on' : 'Edge-pan off'}
                style={{ ...iconBtn, background: panEnabled ? 'rgba(255,255,255,0.22)' : 'transparent', opacity: zoom > 1 ? 1 : 0.55 }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="5 9 2 12 5 15" />
                  <polyline points="9 5 12 2 15 5" />
                  <polyline points="15 19 12 22 9 19" />
                  <polyline points="19 9 22 12 19 15" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <line x1="12" y1="2" x2="12" y2="22" />
                </svg>
              </button>

              <button type="button" onClick={() => setExpanded(false)} aria-label="Collapse zoom controls" title="Collapse" style={iconBtn}>✕</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ScreenZoom
